import { useForm, useFieldArray, Controller } from "react-hook-form";
import {  Box, Flex, Spinner, Checkbox } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import* as z from "zod";
import {isValidCpfOrCnpj,isValidCPF, isValidCNPJ } from "../../../utils/validationDocument"
import {cepMask } from "../../../utils/masks"
import {
  Button,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react"
import { Input } from "../../../components/Forms/Input";
import { DocumentInput } from "../../../components/Forms/DocumentInput";
import { PasswordInput } from "../../../components/Forms/PasswordInput";
import { IoArrowBack, IoArrowForward, IoTrashOutline, IoSearchOutline, IoAddOutline } from "react-icons/io5";
import { FaUser, FaEnvelope, FaGlobe, FaHome, FaIdCard, FaBuilding } from "react-icons/fa";


import { useNavigate } from "react-router-dom";
import { SelectButtonV2 } from "../../../components/Forms/SelectButtonV2";
import { useSeller } from "../../../hooks/useSeller";
import { clearMask, formatDateToAPI, formatPixKeyMasks, formatPixKeyPlaceholders, generateValidCPF, randomBirthdate, randomName, randomPhone, randomString, showToast } from "../../../utils";
import axios from "axios";
import { Banks } from "../../../models/Banks";
import { StepComponentNew } from "../../../components/Forms/StepComponentNew";
import { cnpj, cpf } from "cpf-cnpj-validator";
import { PhoneInput2 } from "../../../components/InputInternationalPhone/InputPhoneNumberInternational";
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';


const companyFormSchema = z.object({
    cnpj: z.string().refine(isValidCpfOrCnpj, {
        message: "Documento inválido",
      }),
    companyName: z.string().min(2, "Razão social é obrigatória").max(255,"Nome da empresa deve ter até 255 caracteres"),
    fantasyName: z.string().min(2, "Nome fantasia é obrigatório").max(255, "Nome fantasia deve ter até 255 caracteres"),
    mccCpf: z.string().optional(),
    mainActivity: z.string()
    .optional(),
    email: z.string().email("Email inválido"),
    sellerUrl: z
    .string()
    .optional()
    .refine(
        (val) => !val || /^[\w.-]+\.[a-z]{2,}/i.test(val) || /^(https?:\/\/)/.test(val),
        "URL inválida"
    )
    .transform((val) => {
        if (!val) return val;
        if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`;
        }
        return val;
    }),
    phone: z.string()
    .min(7, "Telefone é obrigatório")
    .nonempty("Telefone é obrigatório"),
    countryCode:z.string().min(1, "Telefone é obrigatório"),
    officeCountryCode:z.string().optional(),
    officePhone: z.string().optional(),
    contactName: z.string().min(2, "Nome do contato é obrigatório").max(255, "Nome do contato deve ter até 255 caracteres"),
    ownerName: z.string().max(255, "Nome do sócio deve ter até 255 caracteres").optional(),
    ownerDocument: z.string().refine(isValidCPF, {
        message: "CPF inválido",
    }).optional(),
    ownerBirthdate: z
    .string()
    .min(10, "Data inválida")
    .refine((dateStr) => {
      const [day, month, year] = dateStr.split("/").map(Number);
      if (!day || !month || !year) return false;
      const d = new Date(year, month - 1, day);
      if (isNaN(d.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const limit = new Date(today);
      limit.setFullYear(today.getFullYear() - 18);
      return d <= limit;
    }, {
      message: "O responsável deve ter mais de 18 anos",
    }),
    password: z.string()
      .min(8, 'A senha precisa ter no mínimo 8 caracteres')
      .max(30, 'A senha precisa ter no máximo 30 caracteres')
      .nonempty("O campo senha é obrigatório!"),
    confirm_password: z.string()
    .nonempty("O campo confirme sua senha é obrigatório!"),
    isPartner: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "As senhas precisam ser iguais",
    path: ["confirm_password"],
  })
  .superRefine((data, ctx) => {
    if (isValidCPF(data.cnpj)) {
      if (!data.mccCpf || data.mccCpf.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A profissão é obrigatório",
          path: ["mccCpf"],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    const digits = data.phone.replace(/\D/g, "");
    const isBR = data.countryCode === "55";
  
    if (isBR && digits.length < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 10,
        inclusive: true,
        message: "Para país 55 (Brasil), o telefone deve ter 10 dígitos (DDD + número).",
        path: ["phone"],
      });
    }
  })
  .superRefine((data, ctx) => {
    if (isValidCNPJ(data.cnpj)) {
      if (!data.ownerDocument || clearMask(data.ownerDocument).length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CPF do sócio é obrigatório",
          path: ["ownerDocument"],
        });
      }
    }
  });
  
const addressFormSchema = z.object({
    zipCode: z.string().refine((val) => (val ?? "").trim().length > 0, "CEP é obrigatório").refine((val) => !val || clearMask(val ?? "").length === 8, "CEP deve ter 8 dígitos"),
    street: z.string().min(1, "Rua é obrigatória"),
    number: z.string().min(1, "Número é obrigatório"),
    complement: z.string().max(30,"o complemento não pode ter mais de 30 digitos").optional(),
    district: z.string().min(1, "Bairro é obrigatório"),
    city: z.string().min(1, "Cidade é obrigatória"),
    state: z.string().min(1, "Estado é obrigatório"),
})

const addressOwnerFormSchema = z.object({
    zipCode: z.string().optional().refine((val) => !val || clearMask(val ?? "").length === 8, "CEP deve ter 8 dígitos"),
    street: z.string().optional(),
    number: z.string().optional(),
    complement: z.string().max(30,"o complemento não pode ter mais de 30 digitos").optional(),
    district: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
})

const ownerFormSchema = z.object({
    name: z.string().min(2, "Nome é obrigatório").max(255, "Nome deve ter até 255 caracteres"),
    document: z.string().min(1, "CPF é obrigatório").refine((val) => {
        const digits = (val || "").replace(/\D/g, "");
        if (digits.length !== 11) return true;
        return isValidCPF(val);
    }, {
        message: "CPF inválido",
    }),
    birthdate: z
    .string()
    .min(10, "Data inválida")
    .refine((dateStr) => {
      const [day, month, year] = dateStr.split("/").map(Number);
      if (!day || !month || !year) return false;
      const d = new Date(year, month - 1, day);
      if (isNaN(d.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const limit = new Date(today);
      limit.setFullYear(today.getFullYear() - 18);
      return d <= limit;
    }, {
      message: "O sócio deve ter mais de 18 anos",
    }),
    partnerType: z.string().optional(),
    address: addressOwnerFormSchema,
})

const ownersStepSchema = z.object({
    owners: z.array(ownerFormSchema).min(1, "Adicione pelo menos um sócio"),
})

type OwnerFormValues = z.infer<typeof ownerFormSchema>
type OwnersStepFormValues = z.infer<typeof ownersStepSchema>

const defaultOwnerValues: OwnerFormValues = {
    name: "",
    document: "",
    birthdate: "",
    partnerType: "",
    address: {
        zipCode: "",
        street: "",
        number: "",
        complement: "",
        district: "",
        city: "",
        state: "",
    },
}
  
const bankingFormSchema = z.object({
    pixCountryCode:z.string().optional(),
    bank: z.string().min(1, "Banco é obrigatório"),
    agency: z.string().min(1, "Agência é obrigatória"),
    agencyDigit: z.string().min(1, "Digito da agência é obrigatória").max(1, "O digito da agência deve ser entre 0 e 9."),
    account: z.string().min(1, "Conta é obrigatória"),
    accountDigit: z.string().min(1, "Digito da conta é obrigatória").max(1, "O digito da conta deve ser entre 0 e 9."),
    accountType: z.string().min(1, "Tipo de conta é obrigatório"),
    pixKeyType: z.string().min(1, "Tipo de chave PIX é obrigatório"),
    pixKey: z.string().optional(),
    transferType: z.string().min(1, "Tipo de transferencia obrigatório"),
  }).superRefine((arg, ctx) => {
    const { pixKeyType, pixKey } = arg;
    if (!pixKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Chave PIX é obrigatória",
        path: ["pixKey"],
      });
    }
    if (pixKeyType === "CPF" || pixKeyType === "CNPJ") {
      if (!cnpj.isValid(pixKey) && !cpf.isValid(pixKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${pixKeyType} deve ser válido`,
          path: ["pixKey"],
        });
      }
    } else if (pixKeyType === "EMAIL") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pixKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "E-mail inválido",
          path: ["pixKey"],
        });
      }
    }
  })
type CompanyFormValues = z.infer<typeof companyFormSchema>
type AddressFormValues = z.infer<typeof addressFormSchema>
type BankingFormValues = z.infer<typeof bankingFormSchema>

type MCCOption = {
    code: string;
    label: string;
  };


  function formatPhoneE164(phone: string): string {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (phone.startsWith('+')) {
      return phone;
    }
  
    return `+${cleaned}`;
  }


  
export function NewSeller() {
    /** Só exibe botão de teste em ambiente HML */
    const isHml = process.env.REACT_APP_API_URL?.includes("hml-bff") ?? false;

    const stepItems = [
        { id: 1, title: "Informações Gerais" },
        { id: 2, title: "Endereço da empresa" },
        { id: 3, title: "Dados dos sócios" },
        { id: 4, title: "Dados bancários" },
    ]
    const mccOptions: MCCOption[] = [
        { code: 'VETERINARY_SERVICES', label: 'VETERINARIA' },
        { code: 'SPECIAL_TRADE_CONTRACTORS', label: 'DEMAIS SVS DE REFORMA E CONSTRUÇÃO NÃO-CLASSIFICADOS' },
        { code: 'TAXI_CABS_AND_LIMOUSINES', label: 'LIMUSINES E TÁXIS' },
        { code: 'MISCELLANEOUS_GENERAL_MERCHANDISE', label: 'LOJA MERCADORIAS GERAIS' },
        { code: 'MISCELLANEOUS_FOOD_SHOPS', label: 'LOJA DE ALIMENTOS VARIADOS' },
        { code: 'TAILORS_SEAMSTRESSES_MENDING', label: 'COSTUREIRAS E ALFAIATES' },
        { code: 'MISCELLANEOUS_APPAREL_SHOPS', label: 'SERVIÇOS GERIAS PARA VESTIMENTA' },
        { code: 'DOOR_TO_DOOR_SALES', label: 'VENDA DIRETA' },
        { code: 'ARTIST_SUPPLY_CRAFT_SHOPS', label: 'PRODUTOS ARTESANAIS' },
        { code: 'BEAUTY_AND_BARBER_SHOPS', label: 'SALAO DE BELEZA / BARBEARIA / DEPILAÇÃO / MANICURE' },
        { code: 'MISCELLANEOUS_PERSONAL_SERVICES', label: 'OUTROS SERVIÇOS PESSOAIS' },
        { code: 'TOWING_SERVICES', label: 'SERVIÇO LIMPEZA E MANUTENÇÃO' },
        { code: 'COMPUTER_MAINTENANCE_REPAIR', label: 'COMPUTADORES: CONSERTOS E REPAROS' },
        { code: 'BUSINESS_SERVICES', label: 'SERVIÇOS DE NEGÓCIOS' },
        { code: 'AUTOMOTIVE_SERVICE_SHOPS', label: 'SERVIÇOS PARA CARROS (NÃO CONCESIONARIA)' },
        { code: 'DOCTORS_AND_PHYSICIANS', label: 'MÉDICOS (CLÍNICAS E CONSULTÓRIOS)' },
        { code: 'DENTISTS_AND_ORTHODONTISTS', label: 'DENTISTAS E ORTODONTISTAS (CLÍNICAS E CONSULTÓRIOS)' },
        { code: 'MEDICAL_SERVICES_HEALTH_PRACTITIONERS', label: 'MEDICINA EM GERAL E PRATICANTES DE SERVIÇOS DE SAÚDE' },
        { code: 'LEGAL_SERVICES_ATTORNEYS', label: 'SERVIÇOS JURÍDICOS - ADVOGADOS' },
        { code: 'PROFESSIONAL_SERVICES', label: 'OUTROS SERVIÇOS PROFISSIONAIS DE ESPECIALIZADOS' }
      ];

    const [isLoading, setIsLoading] = useState(false);
    const [ cnpjInformation,setCnpjInformation] = useState(
        { address: {} as AddressFormValues,contact : {} as CompanyFormValues, partners : [],
        companyName:"",
        fantasyName:"",
    partnes:[]})
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [ownerCepLoadingIndex, setOwnerCepLoadingIndex] = useState<number | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [ip, setIP] = useState("");
    const navigate = useNavigate();
    const { postSeller, consultCep, sellerCnpjSearch } = useSeller();
    const [loading,setLoading] = useState(false);

    const [formData, setFormData] = useState({
        address: {} as AddressFormValues,
        owners: [] as Array<{ name: string; document: string; birthdate: string; partnerType: string; address: { zipCode: string; street: string; number: string; complement?: string; district: string; city: string; state: string; country: string } }>,
    })

  

    const getData = async () => {
        const res = await axios.get("https://geolocation-db.com/json/");
        setIP(res.data.IPv4);
    };

    useEffect(() => {
        getData();
    //eslint-disable-next-line
    }, []);

    const companyForm = useForm<CompanyFormValues>({
      resolver: zodResolver(companyFormSchema),
      shouldFocusError: false,
    })
    
    const addressForm = useForm<AddressFormValues>({
        resolver: zodResolver(addressFormSchema),
        shouldFocusError: false,
    })
    
    const bankingForm = useForm<BankingFormValues>({
        resolver: zodResolver(bankingFormSchema),
        shouldFocusError: false,
    })

    const ownersForm = useForm<OwnersStepFormValues>({
        resolver: zodResolver(ownersStepSchema),
        defaultValues: { owners: [defaultOwnerValues] },
        mode: "onChange",
        shouldFocusError: false,
    })

    const { fields: ownerFields, append: appendOwner, remove: removeOwner } = useFieldArray({
        control: ownersForm.control,
        name: "owners",
    })

    const transferTypeOptions = [
        {
            label: "Pix",
            value: "PIX"
        },
        {
            label: "Conta bancária",
            value: "BANK_ACCOUNT"
        },
    ]
    const cep = addressForm.watch("zipCode")
    const ownerCepFetchedRef = useRef<Record<number, string>>({})

    async function handleGetCep() {
        try {
            addressForm.clearErrors("zipCode")
            setIsCepLoading(true);
            const { data } = await consultCep(clearMask(cep));
            addressForm.setValue("street", data.logradouro);
            addressForm.setValue("district", data.bairro);
            addressForm.setValue("city", data.localidade);
            addressForm.setValue("state", data.uf);
            addressForm.setValue("complement", data.complemento);
        } catch {
            if (cep.length !== 10) return addressForm.setError("zipCode", { type: "custom", message: "Cep inválido." })
        } finally {
            setIsCepLoading(false);
        }
    }
    
    useEffect(()=>{
        if(clearMask(cep)?.length >= 8){
            handleGetCep();
        }
        //eslint-disable-next-line
    },[cep])

    async function handleOwnerZipCodeLookup(zip: string, index: number) {
        const zipDigits = clearMask(zip ?? "");
        if (zipDigits.length !== 8) return;
        setOwnerCepLoadingIndex(index);
        try {
            const { data } = await consultCep(zipDigits);
            const street = (data.logradouro ?? "").trim();
            const district = (data.bairro ?? "").trim();
            const city = (data.localidade ?? "").trim();
            const state = (data.uf ?? "").trim();
            const complement = (data.complemento ?? "").trim();
            ownersForm.setValue(`owners.${index}.address.street`, street, { shouldValidate: true, shouldDirty: true });
            ownersForm.setValue(`owners.${index}.address.district`, district, { shouldValidate: true, shouldDirty: true });
            ownersForm.setValue(`owners.${index}.address.city`, city, { shouldValidate: true, shouldDirty: true });
            ownersForm.setValue(`owners.${index}.address.state`, state, { shouldValidate: true, shouldDirty: true });
            ownersForm.setValue(`owners.${index}.address.complement`, complement, { shouldValidate: true, shouldDirty: true });
            ownersForm.trigger(`owners.${index}.address`, { shouldFocus: false });
        } catch {
            ownerCepFetchedRef.current[index] = "";
            if (zip.length >= 8) {
                ownersForm.setError(`owners.${index}.address.zipCode`, { type: "custom", message: "CEP inválido." });
            }
        } finally {
            setOwnerCepLoadingIndex(null);
        }
    }
    
    const pixKeyTypeWatching = bankingForm.watch("pixKeyType");
    const companyDocWatching = companyForm.watch("cnpj");
    const emailWatching = companyForm.watch("email") 
    const phoneNumberWatching = companyForm.watch("phone")
    console.log(phoneNumberWatching)


    useEffect(() => {
        if (pixKeyTypeWatching === "CNPJ" || "CPF") {
          bankingForm.setValue("pixKey", companyDocWatching ?? "", {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      if (pixKeyType === "EMAIL") {
        bankingForm.setValue("pixKey", emailWatching ?? "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (pixKeyType === "PHONE") {
        bankingForm.setValue("pixKey", phoneNumberWatching ?? "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      
        //eslint-disable-next-line
    }, [pixKeyTypeWatching]);

    // Carrega os dados do sócio principal no formulário de sócios
    const isPartner = companyForm.watch("isPartner");
    const contactNameForOwner = companyForm.watch("contactName");
    const ownerBirthdateForOwner = companyForm.watch("ownerBirthdate");
    const ownerDocumentForOwner = companyForm.watch("ownerDocument");
    useEffect(() => {
        if (currentStep !== 3 || !isPartner) return;
        if (contactNameForOwner?.trim()) {
            ownersForm.setValue("owners.0.name", contactNameForOwner.trim());
        }
        if (ownerBirthdateForOwner?.trim()) {
            ownersForm.setValue("owners.0.birthdate", ownerBirthdateForOwner.trim());
        }
        if (ownerDocumentForOwner?.trim()) {
            ownersForm.setValue("owners.0.document", ownerDocumentForOwner.trim());
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isPartner, contactNameForOwner, ownerBirthdateForOwner]);

    function onCompanySubmit(data: CompanyFormValues) {
        const hostNameArr = window.location?.host?.split('.');
        hostNameArr.shift();
        const isCpf = isValidCPF(data.cnpj);
      
        const formatData = {
          document: clearMask(data.cnpj),
          companyName: data.companyName,
          mccCpf: isValidCPF(data.cnpj) ? data?.mccCpf : null,
          mainActivity: data.mainActivity || null,
          owner: {
            name: isCpf ? data.companyName : (data.ownerName ?? data.contactName),
            document: isCpf ? clearMask(data.cnpj) : clearMask(data.ownerDocument ?? ""),
            birthdate: formatDateToAPI( data.ownerBirthdate),
          },
          fantasyName: data.fantasyName,
          domainUrl: window.location?.host === "localhost:3000" ? "barte.com" : hostNameArr?.join(".") === "barte.corp" ? "barte.com" : hostNameArr?.join(".") ?? "barte.com",
          sellerUrl: data.sellerUrl,
          userAdmin: {
            email: data.email,
            password: data.password
          },
          contact: {
            name: data.contactName,
            email: data.email,
            countryCode: data.countryCode,
            phone: clearMask(data.phone),
          }
        };
      
        setFormData((prev) => ({ ...prev, ...formatData }));
        setCurrentStep(2);
      }

    function onAddressSubmit(data: AddressFormValues) {
        const formatData = {
            zipCode: clearMask(data.zipCode),
            street: data.street,
            number: data.number,
            complement: data.complement,
            district: data.district,
            city: data.city,
            state: data.state,
            country: "BR",
        }
        setFormData((prev) => ({ ...prev, address: formatData }))
        setCurrentStep(3)
    }

    function onOwnersStepSubmit(data: OwnersStepFormValues) {
        const formatted = data.owners.map((o) => ({
            name: o.name,
            document: clearMask(o.document),
            birthdate: formatDateToAPI(o.birthdate),
            partnerType: o.partnerType,
            address: {
                zipCode: clearMask(o.address.zipCode),
                street: o.address.street,
                number: o.address.number,
                complement: o.address.complement,
                district: o.address.district,
                city: o.address.city,
                state: o.address.state,
                country: "BR",
            },
        }));
        setFormData((prev) => ({ ...prev, owners: formatted }));
        setCurrentStep(4);
    }

    function fillTestData() {
        if (!isHml) return;
        const randomCompanyName = "Empresa " + randomString(6).toUpperCase();
        const randomFantasyName = "Fantasia " + randomString(6).toUpperCase();
        const randomMainActivity = String( Math.floor(4000000 + Math.random() * 999999) );
        const randomEmail = `user_${randomString(5)}@example.com`;
        const randomSellerUrl = `https://www.${randomString(8)}.com`;
        const randomPhoneNumber = randomPhone();
        const randomContactName = randomName();
        const randomOwnerName = randomName();
        const randomOwnerDoc = generateValidCPF();
        const randomCountryCode = "55";
        const randomOfficePhone = randomPhone();
        const randomOfficeCountryCode = "55";
        const randomBirth = randomBirthdate();
        const randomMccCpf = "";

        companyForm.reset({
            companyName: randomCompanyName,
            fantasyName: randomFantasyName,
            mainActivity: randomMainActivity,
            email: randomEmail,
            sellerUrl: randomSellerUrl,
            phone: randomPhoneNumber,
            countryCode: randomCountryCode,
            contactName: randomContactName,
            ownerBirthdate: randomBirth,
            isPartner: true,
            ownerName: randomOwnerName,
            ownerDocument: randomOwnerDoc,
            officePhone: randomOfficePhone,
            officeCountryCode: randomOfficeCountryCode,
            password: "teste@123",
            confirm_password: "teste@123",
            mccCpf: randomMccCpf,
        });
        // Step 2 - Endereço
        addressForm.reset({
            zipCode: "01310-100",
            street: "Avenida Paulista",
            number: "1000",
            complement: "Sala 101",
            district: "Bela Vista",
            city: "São Paulo",
            state: "SP",
        });
        // Step 4 - Dados bancários
        bankingForm.reset({
            bank: "341",
            accountType: "CHECKING_ACCOUNT",
            agency: "1234",
            agencyDigit: "5",
            account: "123456",
            accountDigit: "6",
            pixKeyType: "EMAIL",
            pixKey: "ultra@ultrabikes.com",
            transferType: "PIX",
        });
        setCurrentStep(1);
        showToast({ type: "success", message: "Formulário preenchido com dados de teste (HML)." });
    }
  
    async function onBankingSubmit(data: BankingFormValues) {
        setFormData((prev) => ({ ...prev, banking: data }));
        const firstOwner = formData.owners?.[0];
        const isDefaultAddress = (address) => {
            if (!address) return true;
            return (
                address.zipCode === "" &&
                address.street === "" &&
                address.number === "" &&
                address.complement === "" &&
                address.district === "" &&
                address.city === "" &&
                address.state === "" &&
                address.country === "BR"
            );
        };

        const ownerPayload = firstOwner
            ? { name: firstOwner.name, document: firstOwner.document, birthdate: firstOwner.birthdate }
            : (formData as any).owner ?? { name: "", document: "", birthdate: "" };

        const pixKeyValue =
            data.pixKeyType === "EMAIL"
                ? (formData as any).contact?.email ?? data.pixKey
                : data.pixKeyType === "PHONE"
                    ? (formData as any).contact?.countryCode && (formData as any).contact?.phone
                        ? String((formData as any).contact.countryCode).replace(/\D/g, "") + clearMask(String((formData as any).contact.phone))
                        : data.pixKey
                    : data.pixKey;
                    
        const shouldIncludeOwners =
            firstOwner && firstOwner.address && !isDefaultAddress(firstOwner.address);

        const finalData = {
            ...formData,
            owner: ownerPayload,
            account: {
                account: {
                    bank: data.bank,
                    issuer: data.agency,
                    issuerDigit: data.agencyDigit,
                    number: data.account,
                    bankDigit: data.accountDigit,
                    accountType: data.accountType,
                },
                pix: {
                    key: clearMask(pixKeyValue),
                    keyType: data.pixKeyType,
                },
                transferType: data.transferType,
            },
            owners: shouldIncludeOwners ? formData.owners : [],
            acceptTerm: {
                ip: ip,
                dateTime: new Date().toISOString(),
                userTerms: true,
                cookiePolicy: true,
                privacyPolicy: true,
            },
        };
        try {
            setIsLoading(true);
            await postSeller(finalData);
            showToast({
                message: "Novo seller criado com sucesso!",
                type: "success",
            });
            navigate(`/seller`);
        } catch (error) {
            //eslint-disable-next-line
            error?.response?.data?.errors.map((error) => {
                showToast({
                    type: "error",
                    message: `${error?.code} - ${error?.description}`,
                });
            });
        } finally {
            setIsLoading(false);
        }
    };

    const pixKeyType = bankingForm.watch("pixKeyType");


    async function getSellerCnpjws(cnpj:string){
        setLoading(true)

        try {
            const response = await sellerCnpjSearch(cnpj)
            setCnpjInformation(response?.data)
            return response.data
        } catch (error) {
            return error
        }finally{
            setLoading(false)
        }
    }

    useEffect(() => {
        if(isValidCPF(companyForm?.watch("cnpj"))) {
            companyForm.setValue("ownerName",companyForm?.watch("contactName"))
            companyForm.setValue("ownerDocument",companyForm?.watch("cnpj")) 
            companyForm.setValue("companyName",companyForm?.watch("contactName")) 
            companyForm.setValue("fantasyName",companyForm?.watch("contactName")) 
            companyForm.setValue("mccCpf",companyForm?.watch("mccCpf")) 
        }if(isValidCNPJ(companyForm?.watch("cnpj"))) {
            getSellerCnpjws(clearMask(companyForm?.watch("cnpj")));
        }
            //eslint-disable-next-line
    },[companyForm.watch("cnpj")])

    useEffect(() => {
        bankingForm.setValue("pixCountryCode", companyForm.watch("officeCountryCode") ?? "55")
        //eslint-disable-next-line
    }, [companyForm.watch("officeCountryCode")])
    

    useEffect(() => {
        if(isValidCPF(companyForm?.watch("cnpj"))) {
            companyForm.setValue("ownerName",companyForm?.watch("contactName"))
            companyForm.setValue("ownerDocument",companyForm?.watch("cnpj")) 
            companyForm.setValue("companyName",companyForm?.watch("contactName")) 
            companyForm.setValue("fantasyName",companyForm?.watch("contactName")) 
            companyForm.setValue("mccCpf",companyForm?.watch("mccCpf")) 
        }
            //eslint-disable-next-line
    },[companyForm.watch("contactName")])
  
      useEffect(() => {
        const data = { ...cnpjInformation };
        const countryCode = data?.contact?.countryCode || "BR"
        const countryCodeNumber = getCountryCallingCode(countryCode as CountryCode)
        if (!data) return;
        companyForm.setValue("companyName", data.companyName ?? "");
        companyForm.setValue("phone",data?.contact?.phone);
        companyForm.setValue("email", data?.contact?.email);
        companyForm.setValue("countryCode",countryCodeNumber)
        companyForm.setValue("fantasyName", data.fantasyName ?? "");
        companyForm.setValue("ownerName", data.partners?.[0]?.name ?? "");
        addressForm.setValue("zipCode", cepMask(data.address?.zipCode) ?? "");
        addressForm.setValue("street", data.address?.street ?? "");
        addressForm.setValue("number", data.address?.number ?? "");
        addressForm.setValue("complement", data.address?.complement ?? "");
        addressForm.setValue("district", data.address?.district ?? "");
        addressForm.setValue("city", data.address?.city ?? "");
        addressForm.setValue("state", data.address?.state ?? "");
        companyForm.setValue("mccCpf", "");


        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [cnpjInformation]); 


    return (
        <>

        {isLoading === true ?  
            <Flex
                flexDir="column"
                align="center"
                h="100%"
                justify="center"
                alignContent="center"
                gap="20px"
            >

                <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="blue.500"
                    size="xl"
                />
                <Text>
                    Estamos cadastrando seu vendedor, aguarde alguns instantes.
                </Text>
            </Flex>:

        <Flex
            flexDir="column"
            align="center"
            h="100vh"
            w="100%"
        >
            <Flex
            position="absolute"
            top="50%"
            left="50%"
            zIndex={999}
            height="150px"
            display={loading?"flex" : "none"}
            >
                <Spinner size="xl" color="blue.500" />
            </Flex>
            
            <Flex
                flexDir="column"
                maxW="1200px"
                w="100%"
                mt={8}
                mb={8}
            >
                <StepComponentNew 
                    section={currentStep}
                    items={stepItems}
                />
                {isHml && (
                    <Flex px={8} pt={2} justify="flex-end">
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="gray"
                            onClick={fillTestData}
                        >
                            Preencher teste (HML)
                        </Button>
                    </Flex>
                )}
                {currentStep === 1 && (
                    <form onSubmit={companyForm.handleSubmit(onCompanySubmit)}>
                    <Box p={8}>
                    <VStack spacing={6} align="stretch">
                        {/* CNPJ/CPF - Full Width */}
                        <DocumentInput
                            label="CNPJ/ CPF*"
                            type="text"
                            name="cnpj"
                            placeholder="00.623.904/0001-73"
                            mask="cpf/cnpj"
                            register={companyForm.register("cnpj")}
                            errors={companyForm.formState.errors.cnpj}
                            leftIcon={FaIdCard}
                        />
                        <Flex
                            display={isValidCPF(companyForm?.watch("cnpj")) ? 'flex' : 'none'}
                            w="100%"
                        >
                            <SelectButtonV2
                                label="Selecione uma profissão"
                                register={companyForm.register("mccCpf")}
                                errors={companyForm.formState.errors.mccCpf}
                            >
                                {mccOptions.map((option) => (
                                    <option key={option.code} value={option.code}>
                                        {option.label}
                                    </option>
                                ))}
                            </SelectButtonV2>
                        </Flex>

                        {/* Razão social e Nome fantasia - Two Columns */}
                        <Flex
                            w="100%"
                            gap="4"
                            display={isValidCNPJ(companyForm?.watch("cnpj")) ? 'flex' : 'none'}
                        >
                            <Input 
                                label="Razão social*"
                                type="text"
                                name="companyName"
                                placeholder="Ultra bikes LTDA"
                                register={companyForm.register("companyName")}
                                errors={companyForm.formState.errors.companyName}
                                leftIcon={FaUser}
                            />
                            <Input 
                                label="Nome fantasia*"
                                type="text"
                                name="fantasyName"
                                placeholder="Ultra bikes"
                                register={companyForm.register("fantasyName")}
                                errors={companyForm.formState.errors.fantasyName}
                                leftIcon={FaUser}
                            />
                        </Flex>
                        {/* E-mail e Celular - Two Columns */}
                        <Flex w="100%" gap="4"> 
                            <Box w="100%">
                                <Input 
                                    label="E-mail*"
                                    type="email"
                                    name="email"
                                    placeholder="ultra@ultrabikes.com"
                                    register={companyForm.register("email")}
                                    errors={companyForm.formState.errors.email}
                                    leftIcon={FaEnvelope}
                                />
                                <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                                    O e-mail informado será usado como login de acesso ao portal do seller.
                                </Text>
                            </Box>
                            <PhoneInput2
                                label="Celular*"
                                type="text"
                                name="phone"
                                placeholder="11 99999 9999"
                                countryCodeName="countryCode"
                                register={companyForm.register("phone")}
                                trigger={() => companyForm.trigger("phone")}
                                error={companyForm.formState.errors.phone}
                                valueData={formatPhoneE164(companyForm.watch("countryCode") + companyForm.watch("phone"))}
                                setValue={companyForm.setValue}
                            />
                        </Flex>

                        {/* Nome contato e Data de nascimento - Two Columns; CPF abaixo do Nome contato (quando CNPJ); checkbox abaixo */}
                        <Flex w="100%" gap="4" align="flex-start">
                            <Box w="100%" flex={1}>
                                <Input 
                                    label="Nome contato*"
                                    type="text"
                                    name="contactName"
                                    placeholder="Name"
                                    register={companyForm.register("contactName")}
                                    errors={companyForm.formState.errors.contactName}
                                    leftIcon={FaUser}
                                />
                                {isValidCNPJ(companyForm?.watch("cnpj")) && (
                                    <Box mt={4}>
                                        <DocumentInput
                                            label="CPF*"
                                            type="text"
                                            name="ownerDocument"
                                            mask="cpf/cnpj"
                                            maxLength={14}
                                            infoTooltip={true}
                                            infoTooltipLabel="Documento do sócio majoritário"
                                            placeholder="123.456.789-00"
                                            register={companyForm.register("ownerDocument")}
                                            errors={companyForm.formState.errors.ownerDocument}
                                            leftIcon={FaIdCard}
                                        />
                                    </Box>
                                )}
                                <Checkbox
                                    mt={3}
                                    isChecked={!!companyForm.watch("isPartner")}
                                    onChange={(e) => companyForm.setValue("isPartner", e.target.checked)}
                                    colorScheme="blackAlpha"
                                >
                                    Sou sócio(a) da empresa.
                                </Checkbox>
                            </Box>
                            <Box w="100%" flex={1}>
                                <Input 
                                    label="Data de nascimento*"
                                    type="text"
                                    maxLength={10}
                                    name="ownerBirthdate"
                                    infoTooltip={isValidCNPJ(companyForm?.watch("cnpj")) ? true : false}
                                    infoTooltipLabel="Data de nascimento do sócio majoritário"
                                    placeholder="10/09/2002"
                                    register={companyForm.register("ownerBirthdate")}
                                    mask="date"
                                    errors={companyForm.formState.errors.ownerBirthdate}
                                />
                            </Box>
                        </Flex>

                        {/* Website (Opcional) */}
                        <Input 
                            label="Website (Opcional)"
                            type="text"
                            name="sellerUrl"
                            placeholder="www.seusite.com"
                            register={companyForm.register("sellerUrl")}
                            errors={companyForm.formState.errors.sellerUrl}
                            leftIcon={FaGlobe}
                        />

                        <Flex w="100%" gap="4">
                            <PhoneInput2
                                label="Telefone de contato"
                                type="text"
                                name="officePhone"
                                countryCodeName="officeCountryCode"
                                placeholder="11 99999 9999"
                                register={companyForm.register("officePhone")}
                                trigger={() => companyForm.trigger("officePhone")}
                                error={companyForm.formState.errors.officePhone}
                                valueData={formatPhoneE164(companyForm.watch("officeCountryCode") + companyForm.watch("officePhone"))}
                                setValue={companyForm.setValue}
                            />
                        </Flex>
                        
                        <PasswordInput
                            label="Senha*"
                            type="password"
                            infoTooltip={true}
                            infoTooltipLabel="A senha deve conter pelo menos 8 caracteres, uma letra maiúscula, uma letra minúscula e um número."
                            name="password"
                            placeholder="Sua senha"
                            register={companyForm.register("password")}
                            errors={companyForm.formState.errors.password}
                            aria-label="Digite sua senha"
                            aria-invalid={companyForm.formState.errors.password ? "true" : "false"}
                            mb="10px"
                        />
                        <PasswordInput
                            label="Confirme sua senha*"
                            type="password"
                            name="confirm_password"
                            placeholder="Sua senha"
                            register={companyForm.register("confirm_password") }
                            errors={companyForm.formState.errors.confirm_password}
                            aria-label="Digite sua senha"
                            aria-invalid={companyForm.formState.errors.confirm_password ? "true" : "false"}
                            mb="10px"
                        />
                    </VStack>
                    </Box>
                    {/* Footer */}
                    <Flex px={8} py={4} justify="space-between" mt={4}>
                        <Button
                            variant="outline"
                            leftIcon={<IoArrowBack />}
                            onClick={() => navigate(-1)}
                        >
                            Voltar
                        </Button>
                        <Button
                            type="submit"
                            bg="gray.800"
                            color="white"
                            rightIcon={<IoArrowForward />}
                            _hover={{ bg: "gray.700" }}
                        >
                            Próximo
                        </Button>
                    </Flex>
                    </form>
                )}
                {currentStep === 2 && (
                    <form onSubmit={addressForm.handleSubmit(onAddressSubmit)}>
                    <Box p={8}>
                    <VStack spacing={6} align="stretch">
                        {/* CEP - full width, ícone casa */}
                        <Input
                            label="CEP"
                            type="text"
                            name="zipCode"
                            placeholder="00000-000"
                            mask="cep"
                            register={addressForm.register("zipCode")}
                            errors={addressForm.formState.errors.zipCode}
                            leftIcon={FaHome}
                        />

                        {/* Rua - full width, ícone casa */}
                        <Input
                            label="Rua"
                            type="text"
                            name="street"
                            placeholder="Nome da Rua"
                            register={addressForm.register("street")}
                            errors={addressForm.formState.errors.street}
                            isDisabled={isCepLoading}
                            leftIcon={FaHome}
                        />

                        {/* Número e Complemento - duas colunas */}
                        <Flex w="100%" gap={4}>
                            <Box flex={1}>
                                <Input
                                    label="Número"
                                    type="text"
                                    name="number"
                                    placeholder="Nº Ultra bikes LTDA"
                                    maxLength={6}
                                    register={addressForm.register("number")}
                                    errors={addressForm.formState.errors.number}
                                    isDisabled={isCepLoading}
                                />
                            </Box>
                            <Box flex={1}>
                                <Input
                                    label="Complemento"
                                    type="text"
                                    name="complement"
                                    maxLength={30}
                                    placeholder="Ultra bikes"
                                    register={addressForm.register("complement")}
                                    errors={addressForm.formState.errors.complement}
                                    isDisabled={isCepLoading}
                                    leftIcon={FaHome}
                                />
                            </Box>
                        </Flex>

                        {/* Bairro - full width, ícone casa */}
                        <Input
                            label="Bairro"
                            type="text"
                            name="district"
                            maxLength={30}
                            placeholder="Centro"
                            register={addressForm.register("district")}
                            errors={addressForm.formState.errors.district}
                            isDisabled={isCepLoading}
                            leftIcon={FaHome}
                        />

                        {/* Cidade e Estado - duas colunas */}
                        <Flex w="100%" gap={4}>
                            <Box flex={1}>
                                <Input
                                    label="Cidade"
                                    type="text"
                                    name="city"
                                    maxLength={100}
                                    placeholder="São Paulo"
                                    register={addressForm.register("city")}
                                    errors={addressForm.formState.errors.city}
                                    isDisabled={isCepLoading}
                                    leftIcon={FaHome}
                                />
                            </Box>
                            <Box flex={1}>
                                <Input
                                    label="Estado"
                                    type="text"
                                    name="state"
                                    textTransform="uppercase"
                                    maxLength={2}
                                    placeholder="SP"
                                    register={addressForm.register("state")}
                                    errors={addressForm.formState.errors.state}
                                    isDisabled={isCepLoading}
                                    leftIcon={FaHome}
                                />
                            </Box>
                        </Flex>
                    </VStack>
                    </Box>
                    {/* Footer */}
                    <Flex px={8} py={4} justify="space-between" mt={4}>
                        <Button
                            variant="outline"
                            leftIcon={<IoArrowBack />}
                            onClick={() => setCurrentStep(1)}
                        >
                            Voltar
                        </Button>
                        <Button
                            type="submit"
                            bg="gray.800"
                            color="white"
                            rightIcon={<IoArrowForward />}
                            _hover={{ bg: "gray.700" }}
                        >
                            Próximo
                        </Button>
                    </Flex>
                    </form>
                )}
                {currentStep === 3 && (
                    <form onSubmit={ownersForm.handleSubmit(onOwnersStepSubmit)}>
                    <Box p={8}>
                    <VStack spacing={6} align="stretch">
                        {ownerFields.map((field, index) => (
                            <Box
                                key={field.id}
                                p={6}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                position="relative"
                            >
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Text fontSize="xl" fontWeight="bold">
                                        Sócio {index + 1}
                                    </Text>
                                    {index >= 1 && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() => removeOwner(index)}
                                            leftIcon={<IoTrashOutline />}
                                        >
                                            Remover sócio
                                        </Button>
                                    )}
                                </Flex>
                                
                                <VStack spacing={4} align="stretch">
                                    <Flex w="100%" gap="4">
                                        <Input 
                                            label="Nome sócio*"
                                            type="text"
                                            maxLength={255}
                                            placeholder="João Barbosa"
                                            register={ownersForm.register(`owners.${index}.name`)}
                                            errors={ownersForm.formState.errors.owners?.[index]?.name}
                                            leftIcon={FaUser}
                                        />
                                        <Controller
                                            control={ownersForm.control}
                                            name={`owners.${index}.document`}
                                            render={({ field, fieldState }) => (
                                                <DocumentInput
                                                    label="CPF do sócio*"
                                                    type="text"
                                                    placeholder="999.999.999-99"
                                                    mask="cpf/cnpj"
                                                    name={field.name}
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error}
                                                    leftIcon={FaIdCard}
                                                />
                                            )}
                                        />
                                    </Flex>

                                    <Flex w="100%" gap="4">
                                        <Input 
                                            label="Data de nascimento*"
                                            type="text"
                                            maxLength={10}
                                            placeholder="10/09/2002"
                                            register={ownersForm.register(`owners.${index}.birthdate`)}
                                            mask="date"
                                            errors={ownersForm.formState.errors.owners?.[index]?.birthdate}
                                        />
                                        <SelectButtonV2
                                            name={`owners.${index}.partnerType`}
                                            label="Vínculo com a empresa"
                                            variant="ghost"
                                            bgColor="#EDF1F7"
                                            h="27px"
                                            register={{ ...ownersForm.register(`owners.${index}.partnerType`) }}
                                            errors={ownersForm.formState.errors.owners?.[index]?.partnerType}
                                        >
                                            <option value="PARTNER">Sócio</option>
                                            <option value="PROXY_HOLDER">Procurador</option>
                                            <option value="LEGAL_REPRESENTATIVE">Represente Legal</option>
                                            <option value="OTHER">Outros</option>
                                        </SelectButtonV2>
                                    </Flex>

                                    <Text fontSize="md" fontWeight="medium" mt={2}>
                                        Endereço
                                    </Text>

                                    <Controller
                                        control={ownersForm.control}
                                        name={`owners.${index}.address.zipCode`}
                                        render={({ field, fieldState }) => (
                                            <Input
                                                label="CEP*"
                                                type="text"
                                                placeholder="00000-000"
                                                mask="cep"
                                                name={field.name}
                                                value={field.value ?? ""}
                                                onChange={field.onChange}
                                                onBlur={(e) => {
                                                    field.onBlur();
                                                    handleOwnerZipCodeLookup(ownersForm.getValues(`owners.${index}.address.zipCode`), index);
                                                }}
                                                errors={fieldState.error}
                                                leftIcon={FaHome}
                                            />
                                        )}
                                    />

                                    <Controller
                                        control={ownersForm.control}
                                        name={`owners.${index}.address.street`}
                                        render={({ field, fieldState }) => (
                                            <Input
                                                label="Rua"
                                                type="text"
                                                placeholder="Nome da Rua"
                                                name={field.name}
                                                value={field.value ?? ""}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                errors={fieldState.error}
                                                isDisabled={ownerCepLoadingIndex === index}
                                                leftIcon={FaHome}
                                            />
                                        )}
                                    />

                                    <HStack w="100%" spacing={2}>
                                        <Controller
                                            control={ownersForm.control}
                                            name={`owners.${index}.address.number`}
                                            render={({ field, fieldState }) => (
                                                <Input
                                                    label="Número"
                                                    type="text"
                                                    placeholder="Nº Ultra bikes LTDA"
                                                    maxLength={30}
                                                    name={field.name}
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error}
                                                    isDisabled={ownerCepLoadingIndex === index}
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={ownersForm.control}
                                            name={`owners.${index}.address.complement`}
                                            render={({ field, fieldState }) => (
                                                <Input
                                                    label="Complemento"
                                                    type="text"
                                                    maxLength={30}
                                                    placeholder="Ultra bikes"
                                                    name={field.name}
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error}
                                                    isDisabled={ownerCepLoadingIndex === index}
                                                    leftIcon={FaHome}
                                                />
                                            )}
                                        />
                                    </HStack>
                                    
                                    <Controller
                                        control={ownersForm.control}
                                        name={`owners.${index}.address.district`}
                                        render={({ field, fieldState }) => (
                                            <Input
                                                label="Bairro"
                                                type="text"
                                                maxLength={30}
                                                placeholder="Centro"
                                                name={field.name}
                                                value={field.value ?? ""}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                errors={fieldState.error}
                                                isDisabled={ownerCepLoadingIndex === index}
                                                leftIcon={FaHome}
                                            />
                                        )}
                                    />
                                    
                                    <HStack w="100%" spacing={4}>
                                        <Controller
                                            control={ownersForm.control}
                                            name={`owners.${index}.address.city`}
                                            render={({ field, fieldState }) => (
                                                <Input
                                                    label="Cidade"
                                                    type="text"
                                                    maxLength={100}
                                                    placeholder="São Paulo"
                                                    name={field.name}
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error}
                                                    isDisabled={ownerCepLoadingIndex === index}
                                                    leftIcon={FaHome}
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={ownersForm.control}
                                            name={`owners.${index}.address.state`}
                                            render={({ field, fieldState }) => (
                                                <Input
                                                    label="Estado"
                                                    type="text"
                                                    textTransform="uppercase"
                                                    maxLength={2}
                                                    placeholder="SP"
                                                    name={field.name}
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error}
                                                    isDisabled={ownerCepLoadingIndex === index}
                                                    leftIcon={FaHome}
                                                />
                                            )}
                                        />
                                    </HStack>
                                </VStack>
                            </Box>
                        ))}

                        <Button
                            maxW="200px"
                            type="button"
                            bg="white"
                            color="black"
                            border="1px solid"
                            borderColor="black"
                            borderRadius="md"
                            leftIcon={<IoAddOutline size={18} />}
                            onClick={() => appendOwner({ ...defaultOwnerValues })}
                            _hover={{ bg: "gray.50" }}
                            fontWeight="normal"
                        >
                            Adicionar sócio
                        </Button>
                    </VStack>
                    </Box>
                    <Flex px={8} py={4} justify="space-between" mt={4}>
                        <Button
                            type="button"
                            variant="outline"
                            leftIcon={<IoArrowBack />}
                            onClick={() => setCurrentStep(2)}
                        >
                            Voltar
                        </Button>
                        <Button
                            type="submit"
                            bg="gray.800"
                            color="white"
                            rightIcon={<IoArrowForward />}
                            _hover={{ bg: "gray.700" }}
                        >
                            Próximo
                        </Button>
                    </Flex>
                    </form>
                )}
                {currentStep === 4 && (
                    <form onSubmit={bankingForm.handleSubmit(onBankingSubmit)}>
                    <Box p={8}>
                    <VStack spacing={6} align="stretch">
                        <Flex 
                            w="100%" 
                            gap={4}
                        >
                            <SelectButtonV2
                                name="bank"
                                label="Banco*"
                                variant="ghost"
                                bgColor="#EDF1F7"
                                h="27px"
                                register={{ ...bankingForm.register("bank") }}
                                errors={bankingForm.formState.errors.bank}
                            >
                                <option value="">Select</option>
                                {Banks.map(bank => <option key={bank.value} value={bank.value}>{bank.label}</option>)}
                            </SelectButtonV2>
                            <SelectButtonV2
                                name="accountType"
                                label="Tipo de conta*"
                                variant="ghost"
                                bgColor="#EDF1F7"
                                h="27px"
                                register={{ ...bankingForm.register("accountType") }}
                                errors={bankingForm.formState.errors.accountType}
                            >
                                <option value="">Select</option>
                                <option value="CHECKING_ACCOUNT">Conta corrente</option>
                                <option value="SAVINGS_ACCOUNT">Conta poupança</option>
                            </SelectButtonV2>
                        </Flex>

                        <Flex 
                            w="100%" 
                            gap={4}
                        >
                            <Input
                                label="Agência*"
                                type="number"
                                name="agency"
                                placeholder="0000"
                                register={bankingForm.register("agency")}
                                errors={bankingForm.formState.errors.agency}
                                leftIcon={FaBuilding}
                            />
                            <Input
                                label="Dígito*"
                                type="number"
                                name="agencyDigit"
                                placeholder="0"
                                register={bankingForm.register("agencyDigit")}
                                errors={bankingForm.formState.errors.agencyDigit}
                                maxLength={1}
                                infoTooltip
                                infoTooltipLabel="Se o dígito for X ou não existir, digite 0."
                                leftIcon={FaBuilding}
                            />
                        </Flex>

                        <Flex 
                            w="100%" 
                            gap={4}
                        >
                            <Input
                                label="Conta*"
                                type="number"
                                name="account"
                                placeholder="000000"
                                register={bankingForm.register("account")}
                                errors={bankingForm.formState.errors.account}
                                leftIcon={FaBuilding}
                            />
                            <Input
                                label="Dígito*"
                                type="number"
                                name="accountDigit"
                                placeholder="0"
                                register={bankingForm.register("accountDigit")}
                                errors={bankingForm.formState.errors.accountDigit}
                                maxLength={1}
                                infoTooltip
                                infoTooltipLabel="Se o dígito for X ou não existir, digite 0."
                                leftIcon={FaBuilding}
                            />
                        </Flex>

                        <Flex 
                            w="100%"
                            gap={4}
                        >
                            <SelectButtonV2
                                name="pixKeyType"
                                label="Tipo de chave pix"
                                variant="ghost"
                                bgColor="#EDF1F7"
                                h="27px"
                                register={{ ...bankingForm.register("pixKeyType") }}
                                errors={bankingForm.formState.errors.pixKeyType}
                            >
                                <option value="ALLEATORY_KEY">Chave Aleatoria</option>
                                <option value={isValidCPF(companyForm.watch("cnpj")) ? "CPF" : "CNPJ"}>{isValidCPF(companyForm.watch("cnpj")) ? "CPF" : "CNPJ"}</option>
                                <option value="EMAIL">E-mail</option>
                                <option value="PHONE">Telefone</option>
                            </SelectButtonV2>
                            <SelectButtonV2
                                name="transferType"
                                label="Tipo de transferencia"
                                variant="ghost"
                                bgColor="#EDF1F7"
                                h="27px"
                                register={{ ...bankingForm.register("transferType") }}
                                errors={bankingForm.formState.errors.transferType}
                            >
                                {transferTypeOptions.map(bank => <option key={bank.value} value={bank.value}>{bank.label}</option>)}
                            </SelectButtonV2>
                        </Flex>
                        {pixKeyTypeWatching === "PHONE" ? (
                            <Flex w="100%" gap={4}>
                                <PhoneInput2
                                    label="Chave pix"
                                    type="text"
                                    name="pixKey"
                                    countryCodeName="pixCountryCode"
                                    placeholder="11 99999 9999"
                                    register={bankingForm.register("pixKey")}
                                    trigger={() => bankingForm.register("pixKey")}
                                    error={bankingForm.formState.errors.pixKey}
                                    valueData={formatPhoneE164(bankingForm.watch("pixCountryCode") + bankingForm.watch("pixKey"))}
                                    setValue={bankingForm.setValue}
                                />
                            </Flex>
                        ) : (
                            <Flex 
                                w="100%" 
                                gap={4}
                            >
                                <Input
                                    label="Chave pix"
                                    type="text"
                                    name="pixKey"
                                    placeholder={formatPixKeyPlaceholders(pixKeyType, companyForm.watch("cnpj"))}
                                    register={bankingForm.register("pixKey")}
                                    mask={formatPixKeyMasks(pixKeyType, companyForm.watch("cnpj"))}
                                    errors={bankingForm.formState.errors.pixKey}
                                    maxLength={pixKeyType === "CPF" ? 14 : pixKeyType === "CNPJ" ? 18 : pixKeyType === "PHONE" ? 15 : 99}
                                    leftIcon={IoSearchOutline}
                                />
                                <Box visibility="hidden" />
                            </Flex>
                        )}
                    </VStack>
                    </Box>
                    {/* Footer */}
                    <Flex px={8} py={4} justify="space-between" mt={4}>
                        <Button
                            variant="outline"
                            leftIcon={<IoArrowBack />}
                            onClick={() => setCurrentStep(3)}
                        >
                            Voltar
                        </Button>
                        <Button
                            type="submit"
                            bg="gray.800"
                            color="white"
                            rightIcon={<IoArrowForward />}
                            isLoading={isLoading}
                            isDisabled={isLoading}
                            _hover={{ bg: "gray.700" }}
                        >
                            Cadastrar vendedor
                        </Button>
                    </Flex>
                    </form>
                )}
            </Flex>
        </Flex>    
         }

        </>
    );
}